import { useState } from 'react';
import type { AgentRosterEntry } from 'types/agents';
import { useDeleteAgentMutation, useUpdateAgentMutation } from 'hooks/useAgents';
import { Modal } from './Modal';
import { MaxChatsStepper, OfficeSelect } from './AgentFields';

export function AgentEditPanel({
  entry,
  onClose,
}: {
  entry: AgentRosterEntry;
  onClose: () => void;
}) {
  const [fullName, setFullName] = useState(entry.fullName ?? '');
  const [alias, setAlias] = useState(entry.alias);
  const [office, setOffice] = useState(entry.office);
  const [maxChats, setMaxChats] = useState(entry.maxChats);
  const update = useUpdateAgentMutation();
  const remove = useDeleteAgentMutation();
  const busy = update.isPending || remove.isPending;

  const save = async () => {
    if (!alias.trim()) return;
    await update.mutateAsync({
      userId: entry.userId,
      patch: { alias: alias.trim(), fullName: fullName.trim() || null, office, maxChats },
    });
    onClose();
  };

  const deactivate = async () => {
    await remove.mutateAsync(entry.userId);
    onClose();
  };

  return (
    <Modal
      title="Redigera agent"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={deactivate}
            disabled={busy}
            className="mr-auto rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-60"
          >
            Ta bort som agent
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy || !alias.trim()}
            className="rounded-lg bg-gray-950 px-3 py-1.5 text-sm font-medium text-white disabled:bg-gray-300"
          >
            Spara
          </button>
        </>
      }
    >
      <div className="mb-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
        {entry.email ?? 'Ingen e-post'} · från inloggning
      </div>

      <label className="mb-1 block text-sm font-medium text-gray-700">Riktigt namn</label>
      <input
        value={fullName}
        onChange={(event) => setFullName(event.target.value)}
        placeholder="t.ex. Kari Lindqvist"
        className="mb-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
      />

      <label className="mb-1 block text-sm font-medium text-gray-700">Chattalias</label>
      <input
        value={alias}
        onChange={(event) => setAlias(event.target.value)}
        placeholder="t.ex. Karl"
        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
      />
      <p className="mb-4 mt-1.5 text-xs text-gray-500">Namnet kunden ser – aldrig det riktiga.</p>

      <div className="flex gap-6">
        <OfficeSelect value={office} onChange={setOffice} />
        <MaxChatsStepper value={maxChats} onChange={setMaxChats} />
      </div>
    </Modal>
  );
}
